// packages/web-ui/src/api/clients/views.ts
//
// W4-S1 — Read-only typed client for the views resource.
// W4-S2 — Adds `saveLayout` (PUT) + `exportSvg` (GET → Blob) per AC #2.
// `listViews` and the per-view metadata calls remain W4-S3's responsibility.

import type { Result } from '@tessa-lean/base';
import { defaultViewScope } from '@tessa-lean/base';
import type {
  ReactFlowLayoutJson,
  ViewLayoutPutResponse,
  ViewRelayoutResponse,
  ViewEditResponse,
  ViewScopePutResponse,
  ViewScope,
  LeadEngine,
} from '@tessa-lean/base/api-schemas/index.js';
import {
  apiCall,
  apiCallBinary,
  type ApiContext,
  type ApiError,
  type BinaryResult,
} from '../client.js';

/**
 * IV3-CARD — 202-accepted response from `POST /api/v1/views/:viewId/image`.
 * The route is rate-limited + hashed-audit only — it does NOT return the
 * produced raster; the image arrives later via the `image:genReady` SSE event.
 */
export interface ImageRenderAccepted {
  accepted: boolean;
  sceneContentHash?: string;
}

/**
 * Fetch the persisted React Flow layout JSON for a given view. Returns
 * `Result.err` for any HTTP failure or network error; the caller renders the
 * UX §6.4.4 error state (`Failed to render view.`).
 */
export async function getLayout(
  ctx: ApiContext,
  viewId: string,
): Promise<Result<ReactFlowLayoutJson, ApiError>> {
  return apiCall<undefined, ReactFlowLayoutJson>(
    ctx,
    'GET',
    `/api/v1/views/${encodeURIComponent(viewId)}/layout`,
  );
}

/**
 * Persist a layout. Throws no exceptions; failures are returned as `Result.err`
 * (Arch §3.4 envelope). On success the response carries `layoutId`, the cache
 * key for the matching `GET /export.svg?layoutId=` call.
 */
export async function saveLayout(
  ctx: ApiContext,
  viewId: string,
  layout: ReactFlowLayoutJson,
): Promise<Result<ViewLayoutPutResponse, ApiError>> {
  return apiCall<ReactFlowLayoutJson, ViewLayoutPutResponse>(
    ctx,
    'PUT',
    `/api/v1/views/${encodeURIComponent(viewId)}/layout`,
    layout,
  );
}

/**
 * Fetch the rendered SVG for a layout. Returns the bytes + a download
 * filename (parsed from `Content-Disposition`, with a stable
 * `<viewId>.svg` fallback).
 */
export async function exportSvg(
  ctx: ApiContext,
  viewId: string,
  layoutId?: string,
): Promise<Result<BinaryResult, ApiError>> {
  const params = new URLSearchParams();
  if (typeof layoutId === 'string' && layoutId.length > 0) {
    params.set('layoutId', layoutId);
  }
  // Cache buster: the demo `NodeAutoViewWriter` rewrites `view.svg` in
  // place when the LLM re-asks the same question, but the browser would
  // otherwise serve the previous response from disk cache for 24h
  // (the legacy `Cache-Control: private, max-age=86400` header), so the
  // SPA never sees the regenerated SVG. The bust query param makes each
  // request URL-unique without changing the server contract.
  params.set('_t', String(Date.now()));
  const qs = `?${params.toString()}`;
  return apiCallBinary(
    ctx,
    'GET',
    `/api/v1/views/${encodeURIComponent(viewId)}/export.svg${qs}`,
    {
      accept: 'image/svg+xml',
      filenameHint: `${viewId}.svg`,
    },
  );
}

/**
 * IV3-CARD — POST the NL "change the diagram" instruction to the IV2 raster
 * route (`POST /api/v1/views/:viewId/image`). The route returns a 202 accept
 * (rate-limited + hashed-audit); the produced raster lands asynchronously via
 * the `image:genReady` SSE event. The instruction is sent as `sceneContent`
 * (the field the server hashes for audit). Returns `Result.err` for any HTTP
 * failure — no exceptions cross the package boundary (CLAUDE.md "Coding
 * Standards").
 */
export async function requestImageEdit(
  ctx: ApiContext,
  viewId: string,
  instruction: string,
): Promise<Result<ImageRenderAccepted, ApiError>> {
  return apiCall<{ sceneContent: string }, ImageRenderAccepted>(
    ctx,
    'POST',
    `/api/v1/views/${encodeURIComponent(viewId)}/image`,
    { sceneContent: instruction },
  );
}

/**
 * Re-lay-out a STORED view in place from a natural-language request (the Views
 * tab's prominent "Adjust layout" box). POSTs the request to
 * `POST /api/v1/views/:viewId/relayout`, which compiles + persists a new
 * `LayoutIntent` and re-emits `kb:viewRendered`. On `applied:true` the caller
 * re-fetches the layout; on `applied:false` + `escalation:'expressiveness-gap'`
 * the deterministic DSL couldn't express the request and the UI offers the LLM
 * visualization. Returns `Result.err` for any HTTP failure — no exceptions
 * cross the package boundary (CLAUDE.md "Coding Standards").
 */
export async function relayout(
  ctx: ApiContext,
  viewId: string,
  request: string,
): Promise<Result<ViewRelayoutResponse, ApiError>> {
  return apiCall<{ request: string }, ViewRelayoutResponse>(
    ctx,
    'POST',
    `/api/v1/views/${encodeURIComponent(viewId)}/relayout`,
    { request },
  );
}

/**
 * The DIAGRAM AGENT for a STORED view — one natural-language box covering all
 * three deterministic edit engines: content (add/remove KB elements shown in
 * this view), scope (hide/collapse), and arrangement (the relayout DSL). POSTs
 * to `POST /api/v1/views/:viewId/edit`. `outOfScope:true` in the response means
 * the request was a model change / general question the agent refuses to touch
 * — the UI points the user at the chat. Returns `Result.err` for any HTTP
 * failure — no exceptions cross the package boundary.
 */
export async function editView(
  ctx: ApiContext,
  viewId: string,
  request: string,
): Promise<Result<ViewEditResponse, ApiError>> {
  return apiCall<{ request: string }, ViewEditResponse>(
    ctx,
    'POST',
    `/api/v1/views/${encodeURIComponent(viewId)}/edit`,
    { request },
  );
}

/**
 * Persist the sticky per-view lead engine (`sketch` ⇄ `llm-image`) via
 * `PUT /api/v1/views/:viewId/lead-engine`. Used by the Views-tab engine switch
 * so re-opening the view remembers which representation to lead with.
 */
export async function setLeadEngine(
  ctx: ApiContext,
  viewId: string,
  leadEngine: LeadEngine,
): Promise<Result<{ leadEngine: LeadEngine }, ApiError>> {
  return apiCall<{ leadEngine: LeadEngine }, { leadEngine: LeadEngine }>(
    ctx,
    'PUT',
    `/api/v1/views/${encodeURIComponent(viewId)}/lead-engine`,
    { leadEngine },
  );
}

/**
 * Phase 2 — deterministically set a view's `viewScope` (hide/collapse) via
 * `PUT /api/v1/views/:viewId/scope` (NO LLM; the chat NL path owns the compile).
 * Sending a no-op scope (both arrays empty) STRIPS any persisted scope. Mirrors
 * `setLeadEngine`. Returns `Result.err` for any HTTP failure — no exceptions
 * cross the package boundary.
 */
export async function setScope(
  ctx: ApiContext,
  viewId: string,
  viewScope: ViewScope,
): Promise<Result<ViewScopePutResponse, ApiError>> {
  return apiCall<{ viewScope: ViewScope }, ViewScopePutResponse>(
    ctx,
    'PUT',
    `/api/v1/views/${encodeURIComponent(viewId)}/scope`,
    { viewScope },
  );
}

/**
 * Phase 2 — the "Show all" reveal: clear a view's scope so every part renders
 * again. Sends the empty (no-op) scope, which the server strips so the on-disk
 * layout becomes byte-identical to a never-scoped view.
 */
export async function clearScope(
  ctx: ApiContext,
  viewId: string,
): Promise<Result<ViewScopePutResponse, ApiError>> {
  return setScope(ctx, viewId, defaultViewScope());
}

/**
 * `POST /api/v1/views` — persist a layout as a NEW editable view (a COPY) and
 * return its id. Backs the "Edit" affordance on the KB-tab context diagram + the
 * chat context card: the caller already holds the deterministic context layout,
 * so this writes a fresh view the SPA then opens at `/views/<viewId>` where the
 * "adjust layout" / diagram-agent composer handles change requests. The original
 * KB entity / response-view is never mutated. Returns `Result.err` for any HTTP
 * failure — no exceptions cross the package boundary (CLAUDE.md "Coding
 * Standards").
 */
export async function createView(
  ctx: ApiContext,
  layout: ReactFlowLayoutJson,
  name?: string,
): Promise<Result<{ viewId: string }, ApiError>> {
  return apiCall<{ layout: ReactFlowLayoutJson; name?: string }, { viewId: string }>(
    ctx,
    'POST',
    '/api/v1/views',
    name !== undefined && name.length > 0 ? { layout, name } : { layout },
  );
}

// AR-5 — `getSysmlSource` (which targeted the now-removed `GET
// /api/v1/views/:viewId/sysml` 501 stub) has been DELETED. "View SysML source"
// now loads the raw `.sysml` via the existing files route
// (`GET /api/v1/files/content?path=…`, `filesApi.getContentReal`) keyed on the
// view manifest's `sysmlSourcePath` — see ChatPanel's `onFetchSysmlSource`
// handler (MAJ-11).

export const viewsApi = {
  getLayout,
  saveLayout,
  exportSvg,
  requestImageEdit,
  relayout,
  editView,
  createView,
  setLeadEngine,
  setScope,
  clearScope,
};
